import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, CreditCard, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Section, Spinner } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { api, brl, dateBR, errMsg } from "@/lib/api";

const STATUS = { authorized: "Ativa", pending: "Aguardando pagamento", cancelled: "Cancelada", payment_failed: "Pagamento recusado", paused: "Pausada", expired_checkout: "Checkout expirado" };

export function Assinatura() {
  const { ent, refresh } = useAuth();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();

  const load = async () => {
    const [p, m] = await Promise.all([api.get("/billing/plans"), api.get("/billing/me")]);
    setPlans(p.data);
    setMe(m.data);
  };
  useEffect(() => {
    const ret = params.get("retorno");
    (ret ? api.get(`/billing/subscriptions/${ret}`).catch(() => {}) : Promise.resolve()).then(load).then(refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.post("/billing/subscribe", { plan_key: key });
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancelar a assinatura? As próximas cobranças serão interrompidas.")) return;
    try { await api.post("/billing/cancel"); toast.success("Assinatura cancelada"); await load(); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!plans || !me) return <Spinner />;
  const sub = me.subscription;
  return (
    <div data-testid="assinatura-page">
      <PageHeader eyebrow="Assinatura" title="Planos e cobrança" subtitle="Assinatura mensal recorrente. Cancele quando quiser." />
      {plans.mode === "simulated" && (
        <div className="mb-6 flex gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="billing-mode-simulated">
          <FlaskConical className="h-4 w-4 shrink-0" /> CHECKOUT SIMULADO (sandbox interno): o Mercado Pago será ativado assim que o Access Token de teste for configurado.
        </div>
      )}
      <Section className="mb-6" testid="current-plan">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Plano atual</div>
            <div className="mt-1 text-2xl font-semibold text-[#F5D77F]" data-testid="current-plan-name">{ent?.name}</div>
            {sub && <div className="mt-1 text-sm text-slate-400">Última assinatura: {sub.plan_name} · <span data-testid="subscription-status">{STATUS[sub.status] || sub.status}</span>{sub.next_payment_date && ` · próxima cobrança ${dateBR(sub.next_payment_date)}`}</div>}
          </div>
          {ent?.status === "active" && ent?.subscription_id && <Button variant="outline" className="border-red-500/40 bg-transparent text-red-300" onClick={cancel} data-testid="cancel-subscription-btn">Cancelar assinatura</Button>}
        </div>
      </Section>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {plans.plans.map((p) => {
          const current = ent?.key === p.key;
          return (
            <div key={p.key} className={`card-p flex flex-col p-6 ${current ? "!border-[#D4AF37]" : ""}`} data-testid={`plan-card-${p.key}`}>
              <div className="text-sm font-semibold uppercase tracking-widest text-[#D4AF37]">{p.name}</div>
              <div className="num mt-3 text-3xl font-semibold">{brl(p.price)}<span className="text-sm font-normal text-slate-400">/mês</span></div>
              <ul className="mt-5 flex-1 space-y-2 text-sm text-slate-300">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />{f}</li>)}</ul>
              <Button className="mt-6" disabled={current || busy} onClick={() => subscribe(p.key)} data-testid={`subscribe-${p.key}-btn`}>{current ? "Plano atual" : busy === p.key ? "Abrindo checkout..." : "Assinar"}</Button>
            </div>
          );
        })}
      </div>
      <Section title="Histórico de cobranças" className="mt-6" testid="invoices">
        {me.invoices.length ? me.invoices.map((i) => (
          <div key={i.id} className="flex justify-between border-b border-[#1E3A6E]/50 py-2 text-sm last:border-0" data-testid="invoice-row">
            <span>{dateBR(i.created_at)} · {i.plan_key}</span><span className={i.status === "approved" ? "text-emerald-300" : "text-red-300"}>{brl(i.amount)} · {i.status === "approved" ? "Aprovado" : "Recusado"}</span>
          </div>)) : <p className="text-sm text-slate-400">Nenhuma cobrança ainda.</p>}
      </Section>
    </div>
  );
}

export function Checkout() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [sub, setSub] = useState(null);
  const id = params.get("sub");
  useEffect(() => { api.get(`/billing/subscriptions/${id}`).then((r) => setSub(r.data)).catch(() => setSub(false)); }, [id]);
  const confirm = async (outcome) => {
    try {
      const { data } = await api.post(`/billing/simulated/${id}/confirm`, { outcome });
      await refresh();
      if (data.status === "authorized") toast.success("Pagamento aprovado! Assinatura ativa."); else toast.error("Pagamento recusado (simulação).");
      nav("/app/assinatura");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (sub === null) return <Spinner />;
  if (!sub) return <p className="text-slate-400">Checkout não encontrado.</p>;
  return (
    <div className="mx-auto max-w-md" data-testid="checkout-page">
      <Section>
        <div className="mb-4 rounded-lg bg-amber-500/10 p-3 text-xs text-amber-200">CHECKOUT SIMULADO — nenhum valor real é cobrado.</div>
        <CreditCard className="h-8 w-8 text-[#D4AF37]" />
        <h2 className="mt-3 text-xl font-semibold">PROSPERA+ {sub.plan_name}</h2>
        <p className="num mt-1 text-2xl text-[#F5D77F]">{brl(sub.amount)}<span className="text-sm text-slate-400"> /mês · recorrente</span></p>
        <p className="mt-3 text-sm text-slate-400">Status: {STATUS[sub.status] || sub.status}</p>
        {sub.status === "pending" && (
          <div className="mt-6 flex flex-col gap-2">
            <Button onClick={() => confirm("approved")} data-testid="sim-approve-btn">Aprovar pagamento (teste)</Button>
            <Button variant="outline" className="border-red-500/40 bg-transparent text-red-300" onClick={() => confirm("rejected")} data-testid="sim-reject-btn">Recusar pagamento (teste)</Button>
          </div>
        )}
      </Section>
    </div>
  );
}
